import React, { useCallback, useRef, useState } from 'react';

// Click-or-drag-and-drop zone for a single PDF. Stateless about uploads —
// caller passes `onFile(file)`, `progress` (0..1|null), `error`. Styling lives
// in LeadBuyerMatches.css under `.lbm-doc-*` so this stays scoped to that page;
// promote to a shared stylesheet if reused elsewhere.

function PdfDropzone({ onFile, progress, error, hint }) {
    const inputRef = useRef(null);
    const [dragging, setDragging] = useState(false);

    const handleSelect = useCallback((e) => {
        const file = e.target.files?.[0];
        if (file) onFile(file);
        e.target.value = '';
    }, [onFile]);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file) onFile(file);
    }, [onFile]);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        setDragging(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false);
    }, []);

    return (
        <>
            <div
                className={`lbm-doc-upload-zone ${dragging ? 'lbm-doc-upload-zone-dragging' : ''}`}
                onClick={() => inputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click(); }}
            >
                <input
                    ref={inputRef}
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={handleSelect}
                    className="view-none"
                />
                {progress !== null && progress !== undefined ? (
                    <div className="lbm-doc-progress">
                        <div
                            className="lbm-doc-progress-bar"
                            style={{ '--lbm-doc-progress': `${Math.round(progress * 100)}%` }}
                        />
                    </div>
                ) : (
                    <span className="body-regular colour-secondary">
                        {hint || 'Drop a PDF here or click to upload (max 10MB)'}
                    </span>
                )}
            </div>
            {error && (
                <span className="label-regular colour-error">{error}</span>
            )}
        </>
    );
}

export default PdfDropzone;
